import { Injector, Signal, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslationKey } from '@tolgee/ngx';
import { Observable, combineLatest, filter, map, mergeMap, of, switchMap, take } from 'rxjs';
import { Language } from '../../model/enums/language';
import { AppConfigService } from '../../services/app-config.service';
import { LanguageService } from '../../services/language.service';
import { TranslationService } from '../../services/translation-service';
import { injectUntilDestroyed } from './inject-until-destroyed';
import { AppConfig } from '../../model/app-config';

export type TranslationSpecification = Parameters<TranslationService['translate$']>[0];

export type TranslationSpecificationWithParamsToTranslate = Omit<TranslationSpecification, 'params'> & {
  params?: { [key: string]: string | number | TranslationSpecification };
};

type TranslateObject =
  | TranslationKey
  | TranslationSpecification
  | TranslationSpecificationWithParamsToTranslate
  | { [key: string]: TranslateObject }

type TranslatedObject<T> = T extends
  | TranslationKey
  | TranslationSpecification
  | TranslationSpecificationWithParamsToTranslate
  ? string
  : {
      [P in keyof T]: T[P] extends TranslationKey
        ? string
        : T[P] extends TranslationSpecification | TranslationSpecificationWithParamsToTranslate
          ? string
          : T[P] extends object
            ? TranslatedObject<T[P]>
            : never;
    };

type TranslateOptions = {
  /**
   * Completes the observable after the first translation is emitted.
   * Default: false
   */
  completeAfterFirstTranslation?: boolean;
  /**
   * Provide a language to translate the translateObject to. If a language is explicitly set on a TranslationSpecification, this will be ignored.
   * Default: The user language
   */
  language?: Language;
  /**
   * Since we have eng-GB as default, the provided/user language might not be loaded yet
   * and the observable might emit this before it is loaded.
   * If true, this ensures the first emitted value is translated to the provided/user language.
   * Default: false
   */
  loadLanguageBeforeTranslation?: boolean;
};

export function injectTranslate() {
  const fallbackTranslation = inject(AppConfigService).appConfig.fallbackTranslation;
  const injector = inject(Injector);
  const languageService = inject(LanguageService);
  const translationService = inject(TranslationService);
  const untilDestroyed = injectUntilDestroyed();

  return {
    /**
     * @param translateObject An object that can be nested and contain TranslationKeys, TranslationSpecifications TranslationSpecificationsWithNestedParams or other TranslateObject objects as values.
     * @param options Options for the behavior of the observable.
     * @returns An observable consisting of the same object structure, but with the TranslationKeys/TranslationSpecifications replaced with their translations.
     * Unsubscribes automatically when the component is destroyed.
     */
    toObservable: <T extends TranslateObject>(
      translateObject: T,
      options?: TranslateOptions
    ): Observable<TranslatedObject<T>> =>
      _toObservable(translateObject, translationService, languageService, fallbackTranslation, options).pipe(
        untilDestroyed()
      ),

    /**
     * @param translateObject An object that can be nested and contain TranslationKeys, TranslationObjects or other TranslateObject objects as values.
     * @param options Options for the behavior of the inner observable.
     * @returns A signal consisting of the same object structure, but with the TranslationKeys/TranslationSpecifications replaced with their translations.
     * Unsubscribes from inner observable and cleans up the signal automatically when the component is destroyed.
     */
    toSignal: <T extends TranslateObject>(
      translateObject: T,
      options?: TranslateOptions
    ): Signal<TranslatedObject<T>> =>
      <Signal<TranslatedObject<T>>>(
        toSignal(_toObservable(translateObject, translationService, languageService, fallbackTranslation, options), {
          injector,
        })
      ),
  };
}

function _toObservable<T extends TranslateObject>(
  translateObject: T,
  translationService: TranslationService,
  languageService: LanguageService,
  fallbackTranslation: string,
  options?: TranslateOptions,
): Observable<TranslatedObject<T>> {
  const translatedObject$ = _translateObject$({
    translateObject,
    translationService,
    fallbackTranslation,
    ...(options?.language ? { providedLanguage: options.language } : {}),
  });

  const providedLanguage = options?.language;
  const loadLanguageBeforeTranslation = options?.loadLanguageBeforeTranslation;

  let observable = translatedObject$;

  if (providedLanguage && loadLanguageBeforeTranslation) {
    observable = languageService.translationsLoaded$.pipe(
      filter((languages) => {
        const isLanguageLoaded = languages.includes(providedLanguage);
        if (!isLanguageLoaded) {
          languageService.loadTranslations(providedLanguage);
        }
        return isLanguageLoaded;
      }),
      switchMap(() => translatedObject$)
    );
  } else if (loadLanguageBeforeTranslation) {
    observable = languageService.userLanguage.pipe(
      filter((userLanguage) => languageService.translationsLoaded$.value.includes(userLanguage)),
      switchMap(() => translatedObject$)
    );
  }

  if (options?.completeAfterFirstTranslation) {
    return observable.pipe(take(1));
  }

  return observable;
}

const _isTranslationSpecification = (value: any): value is TranslationSpecification =>
  !!value && typeof value === 'object' && value.hasOwnProperty("translationKey");
const _isTranslationKey = (value: any): value is TranslationKey => typeof value === 'string';
const _isTranslateObject = (value: any): value is TranslateObject =>
  !!value && typeof value === 'object' && !Array.isArray(value) && !value.hasOwnProperty("translationKey");

function _translateObject$<T extends TranslateObject>({
  translateObject,
  translationService,
  fallbackTranslation,
  providedLanguage,
}: {
  translateObject: T;
  translationService: TranslationService;
  fallbackTranslation: string;
  providedLanguage?: Language;
}): Observable<TranslatedObject<T>> {
  if (translateObject === undefined || translateObject === null) {
    console.error(`Translation Service: translateObject is null or undefined.\nFallback translation will be used instead: ${fallbackTranslation}`);
    return of(fallbackTranslation) as Observable<TranslatedObject<T>>;
  } else if (_isTranslationKey(translateObject)) {
    return translationService.translate$({
      translationKey: translateObject,
      ...(providedLanguage ? { language: providedLanguage } : {}),
    }) as Observable<TranslatedObject<T>>;
  } else if (_isTranslationSpecification(translateObject)) {
    const translationKey = translateObject.translationKey;
    if (translationKey === undefined || translationKey === null) {
      console.error(`Translation Service: translationKey value is null or undefined.\nFallback translation will be used instead: ${fallbackTranslation}`);
      return of(fallbackTranslation) as Observable<TranslatedObject<T>>;
    }
    const language = translateObject.language
      ? { language: translateObject.language }
      : providedLanguage
        ? { language: providedLanguage }
        : {};
    const params = translateObject.params ? { params: { ...translateObject.params } } : {};
    if (typeof params.params === 'object' && Object.values(params.params as any).some(_isTranslationSpecification)) {
      const paramsToTranslate$ = Object.entries(params.params as any).reduce(
        (acc, [key, value]) => {
          if (_isTranslationSpecification(value)) {
            return { ...acc, [key]: translationService.translate$(value) };
          }
          return acc;
        },
        {} as { [key: string]: Observable<string> }
      );
      return combineLatest(paramsToTranslate$).pipe(
        mergeMap((translatedParams) => {
          params.params = { ...params.params, ...translatedParams };
          const translate$Params = { translationKey, ...language, ...params };
          return translationService.translate$(translate$Params);
        })
      ) as Observable<TranslatedObject<T>>;
    }
    const translate$Params = { translationKey, ...language, ...params };
    return translationService.translate$(translate$Params) as Observable<TranslatedObject<T>>;
  }
  const keys = Object.keys(translateObject);
  const observables = keys.map((key) => {
    const value = (<{ [key: string]: TranslateObject }>translateObject)[key];
    if (_isTranslationSpecification(value)) {
      const translationKey = value.translationKey;
      if (translationKey === undefined || translationKey === null) {
        console.error(`Translation Service: translationKey value is null or undefined.\nFallback translation will be used instead: ${fallbackTranslation}`);
        return of({ [key]: fallbackTranslation }) as Observable<TranslatedObject<T>>;
      }
      const language = value.language
        ? { language: value.language }
        : providedLanguage
          ? { language: providedLanguage }
          : {};
      const params = value.params ? { params: value.params } : {};
      if (typeof params.params === 'object' && Object.values(params.params as any).some(_isTranslationSpecification)) {
        const paramsToTranslate$ = Object.entries(params.params as any).reduce(
          (acc, [key, value]) => {
            if (_isTranslationSpecification(value)) {
              return { ...acc, [key]: translationService.translate$(value) };
            }
            return acc;
          },
          {} as { [key: string]: Observable<string> }
        );
        return combineLatest(paramsToTranslate$).pipe(
          mergeMap((translatedParams) => {
            params.params = { ...params.params, ...translatedParams };
            const translate$Params = { translationKey, ...language, ...params };
            return translationService
              .translate$(translate$Params)
              .pipe(map((translatedValue) => ({ [key]: translatedValue })));
          })
        );
      }
      const translate$Params = { translationKey, ...language, ...params };
      return translationService
        .translate$(translate$Params)
        .pipe(map((translatedValue) => ({ [key]: translatedValue })));
    } else if (_isTranslationKey(value)) {
      const translationKey = value;
      const language = providedLanguage ? { language: providedLanguage } : {};
      const translate$Params = { translationKey, ...language };
      return translationService
        .translate$(translate$Params)
        .pipe(map((translatedValue) => ({ [key]: translatedValue })));
    } else if (_isTranslateObject(value)) {
      const translateObject = value;
      const translateObject$Params = {
        translateObject,
        translationService,
        fallbackTranslation,
        ...(providedLanguage ? { providedLanguage } : {}),
      };
      return _translateObject$(translateObject$Params).pipe(
        map((translatedNestedObject) => ({ [key]: translatedNestedObject }))
      );
    } else if (key === 'translationKey' && (value === undefined || value === null)) {
      console.error(`Translation Service: translationKey value is null or undefined.\nFallback translation will be used instead: ${fallbackTranslation}`);
      return of({ [key]: fallbackTranslation });
    } else {
      console.error(`Translation Service: Unexpected value type for ${key}: ${typeof value}.\nFallback translation will be used instead: ${fallbackTranslation}`);
      return of({ [key]: fallbackTranslation });
    }
  });

  return combineLatest(observables).pipe(map((arr) => (<any[]>arr).reduce((acc, curr) => ({ ...acc, ...curr }), {})));
}
